/**
 * URLs for the Lumen staff product (Laravel + Inertia).
 *
 * Cross-app auth: family app (Firebase) and Lumen (Laravel session) are different
 * origins. Bare `location.href = lumenUrl(...)` leaves staff on Lumen's login page.
 * Use {@link bridgeToLumen} to form-POST the Firebase ID token to `/auth/firebase`
 * (first-party on Lumen) so they land already signed in.
 *
 * Never fall back to a bare Lumen URL when the bridge fails — that strands
 * people on /lumen/login. Callers should toast and stay in the family app.
 */

import type { User } from 'firebase/auth'

export function lumenBaseUrl(): string {
  const fromEnv = import.meta.env.VITE_BEACON_LUMEN_URL as string | undefined
  if (fromEnv && fromEnv.trim() !== '') {
    return fromEnv.replace(/\/$/, '')
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  ) {
    return 'http://localhost:8000'
  }
  if (typeof window !== 'undefined' && window.location.hostname.startsWith('davids-beacon--')) {
    return 'https://staging-lumen.davidsbeacon.com'
  }
  return 'https://lumen.davidsbeacon.com'
}

/**
 * Absolute URL into Lumen.
 * @param path default `/lumen` (dashboard). Pass full path e.g. `/lumen/safety/sos`.
 */
export function lumenUrl(path = '/lumen'): string {
  const base = lumenBaseUrl()
  const clean = !path ? '/lumen' : path.startsWith('/') ? path : `/${path}`
  return `${base}${clean}`
}

function normalizeLumenPath(path = '/lumen'): string {
  const clean = !path ? '/lumen' : path.startsWith('/') ? path : `/${path}`
  return clean.startsWith('/lumen') ? clean : '/lumen'
}

/** True when `url` is on the configured Lumen origin. */
export function isLumenAbsoluteUrl(url: string): boolean {
  if (!isAbsoluteHttpUrl(url)) return false
  try {
    return new URL(url).origin === new URL(lumenBaseUrl()).origin
  } catch {
    return false
  }
}

export type BridgeToLumenResult =
  | { ok: true }
  | { ok: false; reason: 'no_user' | 'error' }

/** Wait until Firebase has a current user (or timeout). */
export async function waitForFirebaseUser(timeoutMs = 5000): Promise<User | null> {
  const { auth } = await import('@/lib/firebase')
  if (auth.currentUser) return auth.currentUser

  const { onAuthStateChanged } = await import('firebase/auth')
  return new Promise((resolve) => {
    let settled = false
    const finish = (user: User | null) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      unsub()
      resolve(user)
    }
    const timer = window.setTimeout(() => finish(auth.currentUser), timeoutMs)
    const unsub = onAuthStateChanged(auth, (user) => {
      if (user) finish(user)
    })
  })
}

/**
 * Establish a Lumen Laravel session from the current Firebase user, then land
 * on `path` (e.g. `/lumen`, `/lumen/members`).
 *
 * Top-level form POST so Set-Cookie is first-party on the Lumen host.
 * On failure returns `{ ok: false }` — does **not** hard-nav to Lumen login.
 */
export async function bridgeToLumen(path = '/lumen'): Promise<BridgeToLumenResult> {
  const intended = normalizeLumenPath(path)

  try {
    const user = await waitForFirebaseUser()
    if (!user) {
      return { ok: false, reason: 'no_user' }
    }

    const idToken = await user.getIdToken(/* forceRefresh */ true)
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = `${lumenBaseUrl()}/auth/firebase`
    form.style.display = 'none'
    form.setAttribute('accept-charset', 'UTF-8')

    const fields: Record<string, string> = {
      id_token: idToken,
      panel: 'lumen',
      intended,
      // So Lumen can send bridge failures back to this family origin (staging channel etc.).
      return_origin: typeof window !== 'undefined' ? window.location.origin : '',
    }
    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    }

    document.body.appendChild(form)
    form.submit()
    return { ok: true }
  } catch {
    return { ok: false, reason: 'error' }
  }
}

/** Map legacy React `/org…` deep links to Lumen paths (same product features). */
export function mapLegacyOrgPathToLumen(path: string): string | null {
  const bare = path.split('?')[0]?.split('#')[0] ?? path
  const table: Record<string, string> = {
    '/org': '/lumen',
    '/org/': '/lumen',
    '/org/team': '/lumen/members',
    '/org/enrolled': '/lumen/enrollees',
    '/org/notes': '/lumen/session-notes',
    '/org/sos': '/lumen/safety/sos',
    '/org/location': '/lumen/safety/location',
    '/org/camera': '/lumen/safety/clips',
    '/org/settings': '/lumen/settings',
    '/org/pending': '/lumen/register/pending',
    '/org/apply': '/lumen/register',
  }
  if (table[bare]) return table[bare]
  if (bare.startsWith('/org/')) return '/lumen'
  return null
}

export function isAbsoluteHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url)
}

/**
 * Navigate in-app. Absolute Lumen URLs use the session bridge (never bare href).
 * If the bridge cannot run, stays put (callers that need feedback should use bridgeToLumen).
 */
export function goToResolvedPath(
  pathOrUrl: string,
  navigate: (to: string, opts?: { replace?: boolean }) => void,
  replace = false,
): void {
  if (isAbsoluteHttpUrl(pathOrUrl)) {
    if (isLumenAbsoluteUrl(pathOrUrl)) {
      try {
        const path = new URL(pathOrUrl).pathname || '/lumen'
        void bridgeToLumen(path)
      } catch {
        void bridgeToLumen('/lumen')
      }
      return
    }
    if (replace) window.location.replace(pathOrUrl)
    else window.location.assign(pathOrUrl)
    return
  }
  navigate(pathOrUrl, { replace })
}

/** Human message for ?lumen_bridge= codes returned from Lumen form errors. */
export function lumenBridgeErrorMessage(code: string | null): string | null {
  if (!code) return null
  switch (code) {
    case 'token':
      return 'Could not open Lumen — your family sign-in expired. Sign in again, then retry.'
    case 'no_org':
      return 'Could not open Lumen — this account is not linked to an organization yet.'
    case 'failed':
      return 'Could not open Lumen. Stay in the family app and try again in a moment.'
    default:
      return 'Could not open Lumen. Stay in the family app and try again.'
  }
}
